import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Grid,
  Group,
  Loader,
  ScrollArea,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Table,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconDownload, IconUpload, IconCheck } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { DATA_USER_TYPES } from "../../data/options";
import MigrationJobsPanel from "./MigrationJobsPanel";
import { partitionMigrationIssues } from "../../helpers/migrationErrorDownload";
import {
  isStepImportLocked,
  shouldPollMigrationJobs,
} from "../../helpers/migrationJobUi";

const AGENT_USER_TYPE_OPTIONS = [
  { value: "STAFF", label: "Staff" },
  ...DATA_USER_TYPES,
];

const INSPAL_EXPORT_INSTRUCTIONS = {
  productTypeGroup: {
    intro: "Download the class of insurance listing file from Inspal. Unique Class Group Codes become Product Type Groups.",
    steps: [
      "Inspal > General Insurance",
      "Master Files > Class of Insurance Setup > Listing",
      "Sorting > Class Code",
      "Print Type > CSV",
    ],
  },
  productType: {
    intro: "Download the class of insurance listing file from Inspal before uploading it here.",
    steps: [
      "Inspal > General Insurance",
      "Master Files > Class of Insurance Setup > Listing",
      "Sorting > Class Code",
      "Print Type > CSV",
    ],
  },
  provider: {
    intro: "Download the insurer listing file from Inspal before uploading it here.",
    steps: [
      "Inspal > General Insurance",
      "Master Files > Insurer Setup > Listing & Mailing Label",
      "Sorting > By Insurer Code",
      "Report Type > Listing",
      "Report Type > csv + w/o Format",
      "Click Download",
    ],
  },
  user: {
    intro: "Download the producer listing file from Inspal before uploading it here.",
    steps: [
      "Inspal > General Insurance",
      "Master Files > Producer Setup > Listing & Mailing Label",
      "Sorting > By Producer Code",
      "Report Type > Listing",
      "Report Type > csv + w/o Format",
      "Click Download",
    ],
  },
  client: {
    intro: "Download the client listing file from Inspal before uploading it here.",
    steps: [
      "Inspal > General Insurance",
      "Master Files > Client Setup > Listing & Mailing Label",
      "Select Report Type: Listing",
      "Select Gender: All",
      "Select Client Type: All",
      "Report Type: csv + w/o Format",
      "Click Download (Have to wait for a moment 1 to 3 minutes — the screen has no response while the file is being created)",
    ],
  },
  debitNote: {
    intro: "Download the policy export file from Inspal before uploading it here.",
    steps: [
      "Inspal > General Insurance",
      "Enquiry / Export > Policy Enquiry / Export",
      "Enter Issue Date range and check the checkbox before the issue date",
      "Search > Click Export after results updated! (Only 30K results can export each time)",
    ],
  },
};

const InspalMigrationWizard = () => {
  const [api] = useServerApi();
  const [sources, setSources] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsRefresh, setJobsRefresh] = useState(0);
  const [activeTab, setActiveTab] = useState("steps");
  const [userRole, setUserRole] = useState("");
  const [userRoleOptions, setUserRoleOptions] = useState([]);
  const [userType, setUserType] = useState("CONSULTANT");
  const [batchSize, setBatchSize] = useState(100);

  useEffect(() => {
    let cancelled = false;

    api.DataMigration.getSources().then((res) => {
      if (!cancelled && res?.data?.sources) {
        setSources(res.data.sources);
      }
    });

    const loadUserRoles = async () => {
      const names = [];
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages && currentPage <= 20) {
        const data = await api.search({
          apiEntity: "userRole",
          pageSize: 100,
          currentPage,
          sort: { by: "name", order: "asc" },
          select: "name",
        });
        if (cancelled) return;

        const docs = data?.docs || [];
        names.push(...docs.map((role) => role?.name).filter(Boolean));
        const limit = data?.limit || 100;
        totalPages = Math.max(Math.ceil((data?.total || 0) / limit), 1);
        currentPage += 1;
        if (!docs.length) break;
      }

      const uniqueNames = [...new Set(names)].sort((a, b) =>
        a.localeCompare(b)
      );

      setUserRoleOptions(uniqueNames);
      setUserRole((current) => {
        if (current && uniqueNames.includes(current)) return current;
        const consultant = uniqueNames.find(
          (name) => name.toLowerCase() === "consultant"
        );
        return consultant || uniqueNames[0] || "";
      });
    };

    loadUserRoles();

    return () => {
      cancelled = true;
    };
  }, []);

  const inspalSource = sources.find((s) => s.id === "inspal");
  const steps = inspalSource?.steps || [];
  const currentStep = steps[activeStep];

  useEffect(() => {
    if (!steps.length) return;
    const doneIndexes = steps
      .map((step, index) =>
        jobs.some((job) => job.step === step.id && job.status === "COMPLETED")
          ? index
          : null
      )
      .filter((index) => index !== null);
    if (doneIndexes.length) {
      setCompletedSteps((prev) => [...new Set([...prev, ...doneIndexes])]);
    }
  }, [jobs, steps]);

  const options = useMemo(
    () => ({ source: "inspal", userRole, userType, batchSize }),
    [userRole, userType, batchSize]
  );

  const resetStepState = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
  };

  const handleStepSelect = (index) => {
    setActiveStep(index);
    resetStepState();
  };

  const runPreview = async () => {
    if (!file || !currentStep) return;
    setLoading(true);
    try {
      const result = await api.DataMigration.preview(
        file,
        currentStep.id,
        options
      );
      if (!result.success) throw new Error(result.message || "Preview failed");
      setPreview(result.data);
      setImportResult(null);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Preview failed",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    if (!file || !currentStep) return;
    setLoading(true);
    try {
      await api.DataMigration.generateTemplate(file, currentStep.id, options);
      showNotification({
        title: "Template generated",
        message: "MBID import template downloaded",
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Template failed",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file || !currentStep) return;
    try {
      const result = await api.DataMigration.importStep(
        file,
        currentStep.id,
        options
      );
      if (!result.success) throw new Error(result.message || "Import failed");
      setImportResult(null);
      setJobsRefresh((value) => value + 1);
      setActiveTab("jobs");
      showNotification({
        title: "Migration started",
        message:
          result.data?.message || "Import is running in the background",
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Import failed",
        message: error.message,
      });
    }
  };

  const migrationIssues = useMemo(
    () => partitionMigrationIssues(importResult?.errors || []),
    [importResult?.errors]
  );

  const headerValidationFailed =
    preview?.headerValidation && preview.headerValidation.valid === false;

  const downloadImportErrors = async () => {
    if (!importResult?.batchId) return;
    try {
      await api.DataMigration.downloadErrors(importResult.batchId);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Download failed",
        message: error.message,
      });
    }
  };

  return (
    <Stack spacing="md">
      <Title order={3}>Inspal Data Migration</Title>
      <Text size="sm" color="dimmed">
        Upload Inspal exports, preview mapped MBID template data, download the
        template for review, then import in dependency order.
      </Text>

      <Tabs variant="outline" value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List mb={46}>
          <Tabs.Tab value="steps">Migration</Tabs.Tab>
          <Tabs.Tab value="jobs">
            Jobs
            {shouldPollMigrationJobs(jobs) && (
              <Badge size="xs" ml={8} color="yellow" variant="filled">
                Running
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="steps">
          <Grid gutter="lg">
            <Grid.Col span={12} md={3}>
              <Stepper
                active={activeStep}
                onStepClick={handleStepSelect}
                orientation="vertical"
                breakpoint="sm"
              >
                {steps.map((step, index) => (
                  <Stepper.Step
                    key={step.id}
                    label={step.label}
                    description={step.fileHint}
                    icon={
                      completedSteps.includes(index) ? (
                        <IconCheck size={16} />
                      ) : (
                        index + 1
                      )
                    }
                  />
                ))}
              </Stepper>
            </Grid.Col>

            <Grid.Col span={12} md={9}>
              {currentStep ? (
                <Stack spacing="md">
                  {INSPAL_EXPORT_INSTRUCTIONS[currentStep.id] && (
                    <Card withBorder padding="lg">
                      <Title order={5}>How to export from Inspal</Title>
                      <Text size="sm" color="dimmed" mt="xs">
                        {INSPAL_EXPORT_INSTRUCTIONS[currentStep.id].intro}
                      </Text>
                      <Stack spacing="xs" mt="md">
                        {INSPAL_EXPORT_INSTRUCTIONS[currentStep.id].steps.map(
                          (step, index) => (
                            <Text key={index} size="sm">
                              {index + 1}. {step}
                            </Text>
                          )
                        )}
                      </Stack>
                    </Card>
                  )}

                  <Card withBorder padding="lg">
                    <Stack spacing="sm">
                      <Group>
                        <Title order={4}>{currentStep.label}</Title>
                        <Badge>{currentStep.fileHint}</Badge>
                      </Group>
                      <Text size="sm">{currentStep.description}</Text>

                    {currentStep.id === "debitNote" && (
                      <NumberInput
                        label="Progress batch size"
                        description="Write job progress every N rows. Higher is faster. Does not change import logic."
                        value={batchSize}
                        onChange={(value) =>
                          setBatchSize(Number(value) > 0 ? Number(value) : 100)
                        }
                        min={1}
                        max={2000}
                        step={50}
                      />
                    )}

                    {currentStep.id === "user" && (
                      <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                        <Select
                          label="Default User Type (agents)"
                          description="Applied to every imported agent."
                          value={userType}
                          onChange={(value) => setUserType(value || "CONSULTANT")}
                          data={AGENT_USER_TYPE_OPTIONS}
                          withinPortal
                          maxDropdownHeight={240}
                          required
                        />
                        <Select
                          label="Default User Role (agents)"
                          description="Must match an existing User Role in MBID."
                          placeholder="Select user role"
                          value={userRole || null}
                          onChange={(value) => setUserRole(value || "")}
                          data={userRoleOptions}
                          searchable
                          required
                          withinPortal
                          maxDropdownHeight={320}
                          dropdownPosition="bottom-start"
                          nothingFound="No user roles found"
                        />
                      </SimpleGrid>
                    )}

                    <Group>
                      <FileButton onChange={setFile} accept=".xls,.xlsx">
                        {(props) => (
                          <Button
                            {...props}
                            leftIcon={<IconUpload size={16} />}
                          >
                            {file ? file.name : "Upload Inspal file"}
                          </Button>
                        )}
                      </FileButton>
                      <Button disabled={!file || loading} onClick={runPreview}>
                        Preview
                      </Button>
                      <Button
                        disabled={!file || loading}
                        color="blue"
                        leftIcon={<IconDownload size={16} />}
                        onClick={downloadTemplate}
                      >
                        Download MBID template
                      </Button>
                      <Button
                        disabled={
                          !file ||
                          loading ||
                          isStepImportLocked(jobs, currentStep.id) ||
                          (currentStep.id === "user" && !userRole) ||
                          headerValidationFailed
                        }
                        color="green"
                        onClick={runImport}
                      >
                        Start Import
                      </Button>
                    </Group>

                    {loading && <Loader size="sm" />}

                    {headerValidationFailed && (
                      <Alert color="red" title="Wrong file for this step">
                        {preview.headerValidation.message}
                      </Alert>
                    )}

                    {preview && !headerValidationFailed && (
                      <Alert color="blue">
                        Total rows: {preview.totalRows} | Warnings:{" "}
                        {preview.warningRows}
                      </Alert>
                    )}

                    {preview &&
                      headerValidationFailed &&
                      preview.totalRows === 0 && (
                        <Text size="sm" color="dimmed">
                          No rows could be mapped because the file headers do not
                          match this step.
                        </Text>
                      )}

                    {preview?.previewRows?.length > 0 && (
                      <Table striped highlightOnHover fontSize="xs">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Preview (first columns)</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.previewRows.map((row, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>
                                {(row.templateRow || []).slice(0, 6).join(" | ")}
                              </td>
                              <td>{row.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}

                    {importResult?.stats && (
                      <Alert color="green" title="Import stats">
                        Created: {importResult.stats.created ?? 0} | Updated:{" "}
                        {importResult.stats.updated ?? 0} | Warnings:{" "}
                        {importResult.stats.warningRows ?? 0} | Failed:{" "}
                        {importResult.stats.failedRows ?? importResult.stats.skipped ?? 0}
                      </Alert>
                    )}

                    {migrationIssues.warnings.length > 0 && (
                      <Alert color="yellow" title="Import warnings">
                        <Stack spacing="xs">
                          <Text size="sm">
                            Rows were imported with missing references. Fix the
                            master data and re-import to fill blank fields.
                          </Text>
                          <ScrollArea h={180}>
                            <Table fontSize="sm">
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Message</th>
                                  <th>Phase</th>
                                </tr>
                              </thead>
                              <tbody>
                                {migrationIssues.warnings.map((entry, index) => (
                                  <tr key={`${entry.code}-${index}`}>
                                    <td>{entry.code}</td>
                                    <td>{entry.message}</td>
                                    <td>{entry.phase || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </ScrollArea>
                        </Stack>
                      </Alert>
                    )}

                    {migrationIssues.failures.length > 0 && (
                      <Alert color="red" title="Import errors">
                        <Stack spacing="xs">
                          <ScrollArea h={180}>
                            <Table fontSize="sm">
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Message</th>
                                  <th>Phase</th>
                                </tr>
                              </thead>
                              <tbody>
                                {migrationIssues.failures.map((entry, index) => (
                                  <tr key={`${entry.code}-${index}`}>
                                    <td>{entry.code}</td>
                                    <td>{entry.message}</td>
                                    <td>{entry.phase || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </ScrollArea>
                        </Stack>
                      </Alert>
                    )}

                    {importResult?.errors?.length > 0 && importResult?.batchId && (
                      <Group position="right">
                        <Button
                          size="xs"
                          variant="light"
                          leftIcon={<IconDownload size={14} />}
                          onClick={downloadImportErrors}
                        >
                          Download error list
                        </Button>
                      </Group>
                    )}

                    </Stack>
                  </Card>
                </Stack>
              ) : (
                <Card withBorder padding="lg">
                  <Text color="dimmed">Loading migration steps...</Text>
                </Card>
              )}
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="jobs" />
      </Tabs>

      <div style={{ display: activeTab === "jobs" ? "block" : "none" }}>
        <MigrationJobsPanel
          api={api}
          refreshToken={jobsRefresh}
          onJobsChange={setJobs}
        />
      </div>
    </Stack>
  );
};

export default InspalMigrationWizard;
